import { useState, useEffect, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { ArrowUp } from 'lucide-react'
import { Header } from './Header'
import { Footer } from './Footer'
import { ChatBot } from '@/components/ChatBot'
import { CookieConsent } from '@/components/CookieConsent'
import { cn } from '@/lib/utils'

export function Layout({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common')
  const location = useLocation()
  const [showScrollTop, setShowScrollTop] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 600)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0 })
  }, [location.pathname])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className="flex min-h-screen flex-col">
      {/* Skip to content link for keyboard/screen reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-primary-500 focus:px-4 focus:py-2 focus:text-white focus:shadow-lg"
      >
        {t('aria.skipToContent')}
      </a>

      <Header />

      {/*
        Solo fade-in di entrata, senza AnimatePresence/exit: con mode="wait"
        l'albero uscente restava montato e ri-matchava il nuovo URL contro le
        route della vecchia lingua, annullando il cambio lingua fuori dalla
        homepage (catch-all → redirect).
      */}
      <main id="main-content" className="flex-1">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.25 }}
        >
          {children}
        </motion.div>
      </main>

      <Footer />

      {/* ChatBot */}
      <ChatBot />

      {/* Cookie Consent */}
      <CookieConsent />

      {/* Scroll to top */}
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
            onClick={scrollToTop}
            aria-label={t('aria.backToTop')}
            className={cn(
              'fixed bottom-24 right-6 z-40 flex h-11 w-11 items-center justify-center',
              'rounded-full bg-secondary-800/90 text-white shadow-lg backdrop-blur-sm',
              'hover:bg-secondary-700 transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-400 focus:ring-offset-2'
            )}
          >
            <ArrowUp className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  )
}
